import type React from "react"
import { useParams } from "react-router"

import { Page } from "@/components"
import { Button } from "@/nzds/inputs/button"

import { ArrowBackRounded } from "@mui/icons-material"
import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material"

import { useGetScheduleMisalignment } from "../../data/use-get-schedule-misalignment"
import { useInsightsNavigate } from "../../data/use-insights-navigate"
import { useSetpointScenarios } from "../../data/use-setpoint-scenarios"
import { Eyebrow, SectionTitle } from "../../homepage/atoms"
import { VirtualSubmeteringHeader } from "../../virtual-submetering-header"
import { ConfidenceBasis } from "./confidence-basis"
import { DeepSankey } from "./deep-sankey"
import { EnduseSourcesInfo } from "./enduse-sources-info"
import { FixRecommendations } from "./fix-recommendations"
import { buildScheduleStory } from "./schedule-story"
import { SetpointScenariosChart } from "./setpoint-scenarios"
import { SiteHeader } from "./site-header"

const SectionHeading: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Typography
    component="h3"
    sx={{ fontSize: "1.125rem", fontWeight: "bold", mb: 1.5 }}
    variant="body1"
  >
    {children}
  </Typography>
)

export const ScheduleMisalignmentDetailRoute: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>()
  const { data: payload, isPending } = useGetScheduleMisalignment()
  // Lazy: the scenario analysis costs ~2s server-side, so it is fetched for
  // the opened site only, never for every row in the list.
  const { setpointScenariosData } = useSetpointScenarios(siteId ?? null)
  const navigate = useInsightsNavigate()

  const back = (
    <Button
      onClick={() => {
        // URL is /neat/actions/schedule-misalignment/:siteId. relative:"path"
        // "../.." drops :siteId + schedule-misalignment -> /neat/actions (the
        // aggregator, which is the ACT list). There is no roster page.
        navigate("../..", { relative: "path" })
      }}
      size="small"
      startIcon={<ArrowBackRounded />}
      sx={{ mb: 1 }}
      variant="text"
    >
      Back to actions
    </Button>
  )

  if (isPending) {
    return (
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: "center", py: 6, justifyContent: "center" }}
      >
        <CircularProgress size={20} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    )
  }

  // Load resolved but no payload -> fetch failure. Distinct from a valid
  // payload whose siteId isn't present (genuine not-found) — never conflate the
  // two (same rule as VarianceRoute).
  if (!payload) {
    return (
      <>
        <VirtualSubmeteringHeader />
        <Page>
          {back}
          <Typography color="error" sx={{ p: 3 }} variant="body1">
            We couldn&apos;t load this action — please try again.
          </Typography>
        </Page>
      </>
    )
  }

  const site = payload.sites.find((s) => s.site_id === siteId)

  if (!site) {
    return (
      <>
        <VirtualSubmeteringHeader />
        <Page>
          {back}
          <Typography color="text.secondary" sx={{ p: 3 }} variant="body1">
            That action isn&apos;t available.
          </Typography>
        </Page>
      </>
    )
  }

  return (
    <>
      <VirtualSubmeteringHeader />
      <Page>
        <Stack spacing={3}>
          <Box>
            {back}
            <Eyebrow>ACT · SCHEDULE MISALIGNMENT</Eyebrow>
            <SectionTitle>{site.site_label}</SectionTitle>
            {site.stale === true && site.data_through != null ? (
              <Chip
                label={`Data through ${site.data_through}`}
                size="small"
                sx={{ mt: 0.5 }}
                variant="outlined"
              />
            ) : null}
          </Box>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            sx={{ alignItems: "stretch", "& > *": { flex: 1, minWidth: 0 } }}
          >
            {site.site_metadata ? (
              <SiteHeader metadata={site.site_metadata} />
            ) : null}
            <ConfidenceBasis periodLabel={payload.period_label} site={site} />
          </Stack>

          <Box>
            <SectionHeading>Why this is happening</SectionHeading>
            <Typography variant="body2">
              {site.story ?? buildScheduleStory(site)}
            </Typography>
          </Box>

          <Box>
            <Stack
              direction="row"
              spacing={0.5}
              // The heading's own bottom margin moves to the row, so the
              // info button stays centred on the heading text.
              sx={{ alignItems: "center", mb: 1.5, "& > h3": { mb: 0 } }}
            >
              <SectionHeading>
                Where the potential savings are — by end-use and time
              </SectionHeading>
              <EnduseSourcesInfo drivers={site.drivers} />
            </Stack>
            <DeepSankey
              drivers={site.drivers}
              impactDollars={site.impact_dollars}
              siteId={site.site_id}
            />
          </Box>

          {setpointScenariosData?.available ? (
            <Box>
              <SectionHeading>Setpoint scenarios</SectionHeading>
              <SetpointScenariosChart
                data={setpointScenariosData}
                periodLabel={payload.period_label}
              />
            </Box>
          ) : null}

          <Box id="how-to-fix">
            <FixRecommendations
              drivers={site.drivers}
              schedule={site.site_metadata?.schedule}
            />
          </Box>
        </Stack>
      </Page>
    </>
  )
}
